/**
 * 洗牌。见 docs/ARCHITECTURE.md §4.4。
 *
 * 铁律 #2：`shuffleDeck(rng)` 是整个引擎**唯一**的随机入口。牌堆一旦生成，
 * 整手牌的演化完全确定，于是 `replayHand(input, actions)` 能逐位复现。
 * 随机源由调用方注入（服务端 CSPRNG / 客户端 WebCrypto / 测试种子）。
 */
import type { CardId } from '../ids.js'
import { NUM_CARDS, deckOf, isValidCardIdFor } from './cards.js'
import type { Rng } from './rng.js'
import { InvariantError, type DeckVariant } from './types.js'

/**
 * Fisher-Yates（从后往前，`rng.nextInt(i + 1)` 而不是 `nextInt(i)`）。
 *
 * 上界写成 `i` 是经典错误：那样 `d[i]` 永远换不到原位，得到的是均匀分布的
 * **错排**而非均匀分布的排列（俗称 Sattolo 算法），52! 个排列里有一大半取不到。
 */
export function shuffleDeck(rng: Rng, variant: DeckVariant = 'standard'): CardId[] {
  const d = deckOf(variant).slice()
  for (let i = d.length - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1)
    // 适配器返回越界值会静默产生一副非法牌堆（重复牌 / undefined），
    // 而它要到摊牌时才暴露。52 次比较换早失败，值得。
    if (!Number.isInteger(j) || j < 0 || j > i) {
      throw new InvariantError(`shuffleDeck: rng.nextInt(${i + 1}) returned ${String(j)}`)
    }
    const tmp = d[i]!
    d[i] = d[j]!
    d[j] = tmp
  }
  return d
}

/**
 * 牌堆形状校验：张数与玩法一致（标准 52、短牌 36）、每张都属于该玩法、无重复。
 * startHand 用它判 INVALID_DECK。
 */
export function isValidDeck(deck: readonly CardId[], variant: DeckVariant = 'standard'): boolean {
  if (deck.length !== deckOf(variant).length) return false
  let lo = 0
  let hi = 0
  for (let i = 0; i < deck.length; i++) {
    const c = deck[i]!
    if (!isValidCardIdFor(c, variant) || c >= NUM_CARDS) return false
    if (c < 32) {
      const bit = 1 << c
      if ((lo & bit) !== 0) return false
      lo |= bit
    } else {
      const bit = 1 << (c - 32)
      if ((hi & bit) !== 0) return false
      hi |= bit
    }
  }
  return true
}
